import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useTechSession from '../../hooks/useTechSession';
import useLanguage from '../../hooks/useLanguage';
import useVoice from '../../hooks/useVoice';
import useAPI from '../../hooks/useAPI';
import TechButton from '../../components/tech/TechButton';
import VoiceButton from '../../components/tech/VoiceButton';
import SafetyAlert from '../../components/tech/SafetyAlert';
import {
  getTechTaskDetail, startTechTask, completeTechTask,
  escalateTechTask, uploadTaskPhoto,
} from '../../services/api';

// Hebrew-friendly names for error codes (so TTS reads them naturally)
const ERROR_NAMES_HE = {
  DRIVE_PEAK_LOAD_HALT: 'עצירת עומס שיא מוטור נסיעה',
  DRIVE_STALL_HALT: 'עצירת תקיעה מוטור נסיעה',
  DRIVE_HIGH_LOAD_HALT: 'עצירת עומס גבוה מוטור נסיעה',
  DRIVE_PEAK_LOAD_GO_HOME: 'חזרה הביתה עקב עומס שיא',
  DRIVE_STALL_GO_HOME: 'חזרה הביתה עקב תקיעה',
  DRIVE_HIGH_LOAD_GO_HOME: 'חזרה הביתה עקב עומס גבוה',
  DRIVE_MOTOR_NOT_ROTATING: 'מוטור נסיעה לא מסתובב',
  DRIVE_MOTOR_LOW_LOAD_LOW_RPM: 'סיבובים נמוכים מוטור נסיעה',
  DRIVE_MOTOR_LOW_LOAD: 'עומס נמוך מוטור נסיעה',
  CLEAN_STALL_HALT: 'עצירת תקיעה מוטור ניקוי',
  CLEAN_MOTOR_ERROR_STAY_HOME: 'שגיאת מוטור ניקוי נשאר בבית',
  CLEAN_MOTOR_RPM_TOO_LOW: 'סיבובים נמוכים מוטור ניקוי',
  CLEAN_MOTOR_UNDERCURRENT: 'זרם נמוך מוטור ניקוי',
  CLEAN_MOTOR_STALL_STAY_HOME: 'תקיעת מוטור ניקוי נשאר בבית',
  CLEAN_STALL_GO_HOME: 'חזרה הביתה עקב תקיעת ניקוי',
  CLEAN_OVERCURRENT_HALT: 'עצירת זרם יתר מוטור ניקוי',
  CLEAN_MOTOR_NOT_ROTATING: 'מוטור ניקוי לא מסתובב',
  BATTERY_VOLTAGE_LOW: 'מתח סוללה נמוך',
  BATTERY_VOLTAGE_CRITICAL: 'מתח סוללה קריטי',
  PCB_COMMUNICATION_ERROR: 'שגיאת תקשורת כרטיס',
  NO_EP_REPLY: 'אין תשובה מנקודת קצה',
  HOME_FLAG_PROBLEM: 'בעיית דגל בית',
  FAR_END_FLAG_PROBLEM: 'בעיית דגל קצה',
  DRIVE_PEAK_LOAD_OBSTACLE: 'מכשול עומס שיא נסיעה',
  DRIVE_STALL_OBSTACLE: 'מכשול תקיעה נסיעה',
  CLEAN_STALL_OBSTACLE: 'מכשול תקיעה ניקוי',
  CLEAN_OVERCURRENT_OBSTACLE: 'מכשול זרם יתר ניקוי',
  DRIVE_HIGH_LOAD_OBSTACLE: 'מכשול עומס גבוה נסיעה',
};

/**
 * Clean text for TTS — replace error codes with readable names,
 * remove special characters that confuse speech synthesis.
 */
function cleanTextForVoice(text, isHe) {
  if (!text) return '';
  let clean = text;

  if (isHe) {
    // Replace error code names with Hebrew equivalents
    for (const [code, heName] of Object.entries(ERROR_NAMES_HE)) {
      // Match both underscore and space versions
      clean = clean.replace(new RegExp(code.replace(/_/g, '[_ ]'), 'gi'), heName);
    }
  }

  // Remove pipe separators (used in enrichment text)
  clean = clean.replace(/\s*\|\s*/g, ', ');

  // Remove remaining underscores in error names
  clean = clean.replace(/([A-Z]+)_([A-Z]+)/g, '$1 $2');

  // Clean up parentheses with technical data
  // e.g., "(2x בשבוע האחרון)" is fine, keep it

  // Remove excessive whitespace
  clean = clean.replace(/\s+/g, ' ').trim();

  return clean;
}

/**
 * Task detail — full repair guide, voice, start/complete/escalate, photo upload.
 *
 * On page load: auto-reads the full robot briefing (summary, faults, history,
 * motor enrichment, location). The "Start" button replays the voice briefing.
 */
export default function TaskDetail() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { session } = useTechSession();
  const { language, t, isRTL } = useLanguage();
  const { speak, speaking, stopSpeaking } = useVoice();
  const autoPlayedRef = useRef(false);

  const [actionLoading, setActionLoading] = useState(false);
  const [findings, setFindings] = useState('');
  const [fixDesc, setFixDesc] = useState('');
  const [partsUsed, setPartsUsed] = useState([]);
  const [showComplete, setShowComplete] = useState(false);
  const [escalateReason, setEscalateReason] = useState('');
  const [showEscalate, setShowEscalate] = useState(false);
  const [safetyAlert, setSafetyAlert] = useState(null);

  const { data: task, loading, error, reload } = useAPI(
    () => getTechTaskDetail(taskId, language),
    [taskId]
  );

  // Build the full voice briefing text from all available data
  const buildFullBriefing = (taskData) => {
    if (!taskData) return '';
    const parts = [];
    const lang = taskData.voice_briefing?.language || language;
    const isHe = lang.startsWith('he');

    // Translate error code to readable name
    const errorName = isHe
      ? (ERROR_NAMES_HE[taskData.primary_error] || taskData.primary_error?.replace(/_/g, ' '))
      : taskData.primary_error?.replace(/_/g, ' ');

    // Robot number + primary error
    parts.push(
      isHe
        ? `רובוט מספר ${taskData.robot_number}. תקלה: ${errorName}.`
        : `Robot number ${taskData.robot_number}. Error: ${errorName}.`
    );

    // Technician brief (the main summary)
    if (taskData.technician_brief) {
      parts.push(taskData.technician_brief.split(' | ')[0]);
    }

    // History
    if (taskData.history) {
      parts.push(taskData.history);
    }

    // Supplementary faults
    if (taskData.supplementary_faults?.length > 0) {
      const faultNames = taskData.supplementary_faults.map(f => {
        if (isHe) {
          // Extract error code from text like "Drive Peak Load Obstacle (2x בשבוע האחרון)"
          const codePart = f.split(' (')[0].replace(/\s+/g, '_').toUpperCase();
          return ERROR_NAMES_HE[codePart] || f;
        }
        return f;
      });
      parts.push(
        isHe
          ? `תקלות נוספות השבוע: ${faultNames.join(', ')}.`
          : `Additional faults this week: ${faultNames.join(', ')}.`
      );
    }

    // Motor enrichment
    if (taskData.motor_enrichment) {
      parts.push(
        isHe
          ? `פרטי תקלת מוטור: ${taskData.motor_enrichment.replace(/\|/g, ',')}. `
          : `Motor fault details: ${taskData.motor_enrichment.replace(/\|/g, ',')}. `
      );
    }

    // Location
    if (taskData.robot_history?.affected_rows?.length > 0) {
      parts.push(
        isHe
          ? `שורות מושפעות: ${taskData.robot_history.affected_rows.join(', ')}.`
          : `Affected rows: ${taskData.robot_history.affected_rows.join(', ')}.`
      );
    }

    // Last repair
    if (taskData.robot_history?.last_repair) {
      const lr = taskData.robot_history.last_repair;
      const dateStr = lr.date?.split('T')[0] || '';
      parts.push(
        isHe
          ? `תיקון אחרון: ${dateStr}.`
          : `Last repair: ${dateStr}.`
      );
    }

    // Clean the entire text for voice clarity
    return cleanTextForVoice(parts.join(' '), isHe);
  };

  // Auto-play voice briefing when task data loads
  useEffect(() => {
    if (task && !autoPlayedRef.current) {
      autoPlayedRef.current = true;
      const fullText = buildFullBriefing(task);
      if (fullText) {
        const lang = task.voice_briefing?.language || language;
        setTimeout(() => speak(fullText, lang, 0.95), 400);
      }
    }
    // eslint-disable-next-line
  }, [task]);

  // Replay full briefing
  const replayBriefing = () => {
    if (speaking) {
      stopSpeaking();
      return;
    }
    const fullText = buildFullBriefing(task);
    if (fullText) {
      const lang = task.voice_briefing?.language || language;
      speak(fullText, lang, 0.95);
    }
  };

  if (!session) { navigate('/tech'); return null; }

  if (loading) return <div style={styles.center}>{t.loading}</div>;
  if (error) return (
    <div style={styles.center}>
      <div style={{ color: '#d63031', marginBottom: 12 }}>{error}</div>
      <TechButton variant="secondary" onClick={reload}>{t.retry}</TechButton>
    </div>
  );
  if (!task) return null;

  const guide = task.repair_guide || {};
  const isInProgress = task.status === 'in_progress';
  const isDone = task.status === 'completed' || task.status === 'escalated';

  const handleStart = async () => {
    setActionLoading(true);
    try {
      await startTechTask({ task_id: taskId, technician_id: session.technicianId });
      reload();
    } catch (e) { alert(e.message); }
    setActionLoading(false);
  };

  const handleComplete = async () => {
    setActionLoading(true);
    try {
      await completeTechTask({
        task_id: taskId,
        technician_id: session.technicianId,
        findings,
        fix_description: fixDesc,
        parts_replaced: partsUsed,
      });
      // Pass the primary error to the interview for gap analysis
      const errorParam = encodeURIComponent(task.primary_error || '');
      navigate(`/tech/interview/${taskId}?error=${errorParam}`);
    } catch (e) { alert(e.message); }
    setActionLoading(false);
  };

  const handleEscalate = async () => {
    setActionLoading(true);
    try {
      await escalateTechTask({
        task_id: taskId,
        technician_id: session.technicianId,
        reason: escalateReason,
      });
      navigate('/tech/tasks');
    } catch (e) { alert(e.message); }
    setActionLoading(false);
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await uploadTaskPhoto(taskId, file);
      alert('Photo uploaded!');
    } catch (err) { alert(err.message); }
  };

  return (
    <div style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      <SafetyAlert alert={safetyAlert} onDismiss={() => setSafetyAlert(null)} t={t} />

      {/* Back button */}
      <button onClick={() => navigate('/tech/tasks')} style={styles.backBtn}>
        {isRTL ? '\u2192' : '\u2190'} {t.tasks}
      </button>

      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
            {t.robot} #{task.robot_number}
          </h2>
          <VoiceButton
            mode="speak"
            active={speaking}
            onClick={replayBriefing}
          />
        </div>
        <div style={{ fontSize: 13, color: '#b1bac4', marginTop: 4 }}>
          {task.primary_error?.replace(/_/g, ' ')}
        </div>
        <div style={styles.badges}>
          <span style={{ ...styles.badge, background: task.priority === 'CRITICAL' ? '#d6303133' : '#30363d', color: task.priority === 'CRITICAL' ? '#d63031' : '#8b949e' }}>
            {task.priority}
          </span>
          <span style={styles.badge}>{task.severity}</span>
          <span style={styles.badge}>{task.category}</span>
        </div>
      </div>

      {/* Summary */}
      <div style={styles.section}>
        <div style={{ fontSize: 14, color: '#b1bac4', lineHeight: 1.5 }}>
          {task.technician_brief}
        </div>
        {task.history && (
          <div style={{ fontSize: 12, color: '#8b949e', marginTop: 8 }}>{task.history}</div>
        )}
      </div>

      {/* Context flags */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {task.is_repeat_location && <span style={{ ...styles.flag, background: '#e1705533', color: '#e17055' }}>Repeat Location</span>}
        {(task.is_chronic || task.robot_history?.is_chronic) && <span style={{ ...styles.flag, background: '#d6303133', color: '#d63031' }}>Chronic</span>}
        {task.weather_factor && <span style={{ ...styles.flag, background: '#fdcb6e33', color: '#fdcb6e' }}>Weather Related</span>}
      </div>

      {/* Supplementary Faults (excluded errors that appeared this week) */}
      {task.supplementary_faults?.length > 0 && (
        <div style={styles.section}>
          <h3 style={{ ...styles.sectionTitle, color: '#fdcb6e' }}>
            {isRTL ? '⚠ תקלות נוספות השבוע' : '⚠ Additional Faults This Week'}
          </h3>
          <div style={{ fontSize: 12, color: '#8b949e', marginBottom: 8 }}>
            {isRTL
              ? 'תקלות שלא יוצרות קריאת שירות אך הופיעו בשבוע האחרון:'
              : 'Faults that don\'t generate service calls but appeared this week:'}
          </div>
          {task.supplementary_faults.map((fault, i) => (
            <div key={i} style={{
              padding: '8px 12px', background: '#fdcb6e11', border: '1px solid #fdcb6e33',
              borderRadius: 8, marginBottom: 6, fontSize: 13, color: '#fdcb6e',
            }}>
              {fault}
            </div>
          ))}
        </div>
      )}

      {/* Motor Fault Enrichment */}
      {task.motor_enrichment && (
        <div style={styles.section}>
          <h3 style={{ ...styles.sectionTitle, color: '#74b9ff' }}>
            {isRTL ? '🔧 פרטי תקלת מוטור' : '🔧 Motor Fault Details'}
          </h3>
          <div style={{
            padding: '10px 12px', background: '#74b9ff11', border: '1px solid #74b9ff33',
            borderRadius: 8, fontSize: 13, color: '#74b9ff', lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
          }}>
            {task.motor_enrichment.split(' | ').map((part, i) => (
              <div key={i} style={{ marginBottom: 4 }}>{part}</div>
            ))}
          </div>
        </div>
      )}

      {/* Additional Data */}
      {task.additional_data && (
        <div style={styles.section}>
          <h3 style={{ ...styles.sectionTitle, color: '#a29bfe' }}>
            {isRTL ? '📋 מידע נוסף' : '📋 Additional Data'}
          </h3>
          <div style={{
            padding: '10px 12px', background: '#a29bfe11', border: '1px solid #a29bfe33',
            borderRadius: 8, fontSize: 13, color: '#b1bac4', lineHeight: 1.5,
          }}>
            {task.additional_data}
          </div>
        </div>
      )}

      {/* Robot History */}
      {task.robot_history && task.robot_history.total_errors_90d > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>
            {isRTL ? 'היסטוריית רובוט' : 'Robot History'}
          </h3>
          <div style={styles.historyGrid}>
            <div style={styles.historyItem}>
              <div style={styles.historyValue}>{task.robot_history.total_errors_90d}</div>
              <div style={styles.historyLabel}>{isRTL ? 'תקלות 90 יום' : 'Errors 90d'}</div>
            </div>
            <div style={styles.historyItem}>
              <div style={{
                ...styles.historyValue,
                color: task.robot_history.total_errors_7d > 5 ? '#d63031' : '#e6edf3',
              }}>{task.robot_history.total_errors_7d}</div>
              <div style={styles.historyLabel}>{isRTL ? 'תקלות 7 ימים' : 'Errors 7d'}</div>
            </div>
            <div style={styles.historyItem}>
              <div style={styles.historyValue}>{task.robot_history.total_repairs}</div>
              <div style={styles.historyLabel}>{isRTL ? 'תיקונים' : 'Repairs'}</div>
            </div>
          </div>

          {/* Top errors */}
          {task.robot_history.top_errors?.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, color: '#8b949e', marginBottom: 6 }}>
                {isRTL ? 'תקלות נפוצות:' : 'Top errors:'}
              </div>
              {task.robot_history.top_errors.map((e, i) => (
                <div key={i} style={styles.historyErrorRow}>
                  <span style={{ fontSize: 13, color: '#b1bac4' }}>
                    {e.code?.replace(/_/g, ' ')}
                  </span>
                  <span style={{
                    fontSize: 12, fontWeight: 700,
                    color: e.count > 10 ? '#d63031' : '#fdcb6e',
                  }}>
                    x{e.count}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Last repair */}
          {task.robot_history.last_repair && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #30363d' }}>
              <div style={{ fontSize: 12, color: '#8b949e', marginBottom: 4 }}>
                {isRTL ? 'תיקון אחרון:' : 'Last repair:'}
              </div>
              <div style={{ fontSize: 13, color: '#b1bac4' }}>
                {task.robot_history.last_repair.date?.split('T')[0]}
              </div>
              {task.robot_history.last_repair.findings?.length > 0 && (
                <div style={{ fontSize: 12, color: '#8b949e', marginTop: 4 }}>
                  {isRTL ? 'ממצאים: ' : 'Findings: '}
                  {task.robot_history.last_repair.findings.join(', ')}
                </div>
              )}
              {task.robot_history.last_repair.parts_replaced?.length > 0 && (
                <div style={styles.chipList}>
                  {task.robot_history.last_repair.parts_replaced.map((p, i) => (
                    <span key={i} style={{ ...styles.chip, background: '#e1705522', color: '#e17055' }}>
                      {typeof p === 'object' ? (p.name || p.partId || JSON.stringify(p)) : p}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Affected rows */}
          {task.robot_history.affected_rows?.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#8b949e' }}>
              {isRTL ? 'שורות מושפעות: ' : 'Affected rows: '}
              {task.robot_history.affected_rows.join(', ')}
            </div>
          )}
        </div>
      )}

      {/* Repair Guide */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>{t.repairGuide}</h3>

        {/* Tools & Parts */}
        {guide.tools_needed?.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, color: '#8b949e', marginBottom: 4 }}>Tools:</div>
            <div style={styles.chipList}>
              {guide.tools_needed.map((tool, i) => <span key={i} style={styles.chip}>{tool}</span>)}
            </div>
          </div>
        )}
        {guide.parts_needed?.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, color: '#8b949e', marginBottom: 4 }}>Parts:</div>
            <div style={styles.chipList}>
              {guide.parts_needed.map((part, i) => <span key={i} style={{ ...styles.chip, background: '#6c5ce722', color: '#a29bfe' }}>{part}</span>)}
            </div>
          </div>
        )}

        {/* Steps */}
        {guide.steps?.map((step, i) => (
          <div key={i} style={styles.step}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', gap: 10, flex: 1 }}>
                <span style={styles.stepNum}>{step.step}</span>
                <div>
                  <div style={{ fontSize: 14, color: '#e6edf3', lineHeight: 1.4 }}>
                    {step.instruction}
                  </div>
                  {step.safety_note && (
                    <div style={{ fontSize: 12, color: '#fdcb6e', marginTop: 4 }}>
                      {step.safety_note}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: '#8b949e', marginTop: 2 }}>
                    ~{step.minutes} {t.minutes}
                  </div>
                </div>
              </div>
              <VoiceButton
                mode="speak"
                size={32}
                active={false}
                onClick={() => speak(step.instruction, language)}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      {!isDone && (
        <div style={{ marginTop: 16 }}>
          {!isInProgress ? (
            <>
              {/* Replay briefing button */}
              <TechButton fullWidth variant="secondary" onClick={replayBriefing} style={{ marginBottom: 10 }}>
                {speaking
                  ? (isRTL ? '⏹ עצור הקראה' : '⏹ Stop Reading')
                  : (isRTL ? '🔊 הקרא שוב' : '🔊 Replay Briefing')
                }
              </TechButton>
              {/* Start task button */}
              <TechButton fullWidth onClick={handleStart} loading={actionLoading}>
                {isRTL ? '▶ התחל עבודה' : '▶ Start Task'}
              </TechButton>
            </>
          ) : (
            <>
              {/* Photo upload */}
              <label style={styles.photoLabel}>
                {t.uploadPhoto}
                <input type="file" accept="image/*" capture="environment" onChange={handlePhoto} style={{ display: 'none' }} />
              </label>

              {/* Complete form */}
              {!showComplete ? (
                <TechButton fullWidth variant="success" onClick={() => setShowComplete(true)} style={{ marginTop: 10 }}>
                  {t.complete}
                </TechButton>
              ) : (
                <div style={styles.section}>
                  <textarea
                    placeholder={t.findings}
                    value={findings}
                    onChange={e => setFindings(e.target.value)}
                    style={styles.textarea}
                    rows={3}
                  />
                  <textarea
                    placeholder="Fix description"
                    value={fixDesc}
                    onChange={e => setFixDesc(e.target.value)}
                    style={styles.textarea}
                    rows={2}
                  />
                  <input
                    placeholder={`${t.partsReplaced} (comma separated)`}
                    value={partsUsed.join(', ')}
                    onChange={e => setPartsUsed(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                    style={styles.input}
                  />
                  <TechButton fullWidth variant="success" onClick={handleComplete} loading={actionLoading} style={{ marginTop: 8 }}>
                    {t.complete}
                  </TechButton>
                </div>
              )}

              {/* Escalate */}
              {!showEscalate ? (
                <TechButton fullWidth variant="warning" onClick={() => setShowEscalate(true)} style={{ marginTop: 10 }}>
                  {t.escalate}
                </TechButton>
              ) : (
                <div style={{ ...styles.section, marginTop: 10 }}>
                  <textarea
                    placeholder="Reason for escalation..."
                    value={escalateReason}
                    onChange={e => setEscalateReason(e.target.value)}
                    style={styles.textarea}
                    rows={2}
                  />
                  <TechButton fullWidth variant="warning" onClick={handleEscalate} loading={actionLoading}>
                    {t.escalate}
                  </TechButton>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  center: { textAlign: 'center', padding: 32, color: '#8b949e' },
  backBtn: {
    background: 'none', border: 'none', color: '#6c5ce7',
    fontSize: 14, cursor: 'pointer', padding: '4px 0', marginBottom: 8,
    fontFamily: 'inherit',
  },
  header: {
    background: '#161b22', borderRadius: 12, padding: '16px',
    marginBottom: 12,
  },
  badges: { display: 'flex', gap: 6, marginTop: 8 },
  badge: {
    fontSize: 11, padding: '3px 8px', borderRadius: 6,
    background: '#30363d', color: '#8b949e',
  },
  flag: {
    fontSize: 11, padding: '3px 8px', borderRadius: 6, fontWeight: 600,
  },
  section: {
    background: '#161b22', borderRadius: 12, padding: '14px 16px',
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 15, fontWeight: 600, margin: '0 0 10px 0', color: '#e6edf3',
  },
  chipList: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 },
  chip: {
    fontSize: 12, padding: '4px 10px', background: '#30363d',
    borderRadius: 16, color: '#b1bac4',
  },
  historyGrid: {
    display: 'flex', gap: 10,
  },
  historyItem: {
    flex: 1, background: '#0d1117', borderRadius: 8, padding: '10px 8px',
    textAlign: 'center', border: '1px solid #30363d',
  },
  historyValue: {
    fontSize: 20, fontWeight: 700, color: '#e6edf3',
  },
  historyLabel: {
    fontSize: 10, color: '#8b949e', marginTop: 2,
  },
  historyErrorRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '4px 0', borderBottom: '1px solid #30363d20',
  },
  step: {
    padding: '10px 0',
    borderBottom: '1px solid #30363d',
  },
  stepNum: {
    width: 24, height: 24, borderRadius: '50%',
    background: '#6c5ce7', color: '#fff', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    fontSize: 12, fontWeight: 700, flexShrink: 0,
  },
  photoLabel: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: 48, padding: '12px 24px', fontSize: 16, fontWeight: 600,
    border: '2px dashed #30363d', borderRadius: 12, color: '#8b949e',
    cursor: 'pointer', width: '100%', boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  textarea: {
    width: '100%', padding: '10px 12px', fontSize: 14, background: '#0d1117',
    border: '1px solid #30363d', borderRadius: 8, color: '#e6edf3',
    resize: 'vertical', marginBottom: 8, fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  input: {
    width: '100%', padding: '10px 12px', fontSize: 14, background: '#0d1117',
    border: '1px solid #30363d', borderRadius: 8, color: '#e6edf3',
    fontFamily: 'inherit', boxSizing: 'border-box',
  },
};
